import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuRefreshCw, LuX } from 'react-icons/lu'
import { useColorModeValue } from './ui/color-mode'
import { toaster } from './ui/toaster'
import { analytics } from '../utils/analytics'
import type { NewVersionEventDetail } from '../hooks/useVersionCheck'

const SESSION_DISMISS_KEY = 'barbermate-stale-build-dismissed'

/**
 * Two-stage stale-build prompt.
 *   1. Info toast on the first navigation after a new build is detected.
 *   2. Persistent amber banner above the app shell once the toast is closed
 *      (or auto-expires) and the user keeps working on the stale build.
 */
export function StaleBuildBanner() {
    const { t } = useTranslation()
    const [detail, setDetail] = useState<NewVersionEventDetail | null>(null)
    const [bannerVisible, setBannerVisible] = useState(false)

    const bg = useColorModeValue('orange.50', 'orange.900')
    const borderColor = useColorModeValue('orange.300', 'orange.600')
    const iconBg = useColorModeValue('orange.400', 'orange.300')
    const iconColor = useColorModeValue('white', 'gray.900')
    const titleColor = useColorModeValue('gray.900', 'gray.50')
    const subColor = useColorModeValue('gray.600', 'gray.300')

    useEffect(() => {
        function onNewVersion(event: Event) {
            const d = (event as CustomEvent<NewVersionEventDetail>).detail
            setDetail(d)

            // If the user already dismissed for this session, jump straight to the banner.
            const dismissedFor = sessionStorage.getItem(SESSION_DISMISS_KEY)
            if (dismissedFor === d.latestSha) {
                setBannerVisible(true)
                return
            }

            let accepted = false
            toaster.create({
                title: t('staleBuild.toast.title', { defaultValue: 'BarberMate just updated.' }),
                description: t('staleBuild.toast.description', {
                    defaultValue: 'Refresh to get build {{sha}}.',
                    sha: d.latestSha,
                }),
                type: 'info',
                duration: 10_000,
                closable: true,
                action: {
                    label: t('staleBuild.refresh', { defaultValue: 'Refresh' }),
                    onClick: () => {
                        accepted = true
                        analytics.versionPromptAccepted(
                            d.currentSha,
                            d.latestSha,
                            Date.now() - d.detectedAt,
                            'toast',
                        )
                        window.location.reload()
                    },
                },
                onStatusChange: ({ status }) => {
                    if (status === 'unmounted' && !accepted) {
                        analytics.versionPromptDismissed(
                            d.currentSha,
                            d.latestSha,
                            Date.now() - d.detectedAt,
                        )
                        // Toast gone, user kept working → show the persistent banner.
                        setBannerVisible(true)
                    }
                },
            })
        }

        window.addEventListener('newVersionAvailable', onNewVersion)
        return () => window.removeEventListener('newVersionAvailable', onNewVersion)
    }, [t])

    if (!detail || !bannerVisible) return null

    return (
        <Flex
            align="center"
            gap="3"
            px="4"
            py="2.5"
            mx="auto"
            my="3"
            maxW="1440px"
            w="auto"
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
        >
            <Flex
                w="30px"
                h="30px"
                align="center"
                justify="center"
                borderRadius="full"
                bg={iconBg}
                color={iconColor}
                flexShrink={0}
            >
                <LuRefreshCw size={15} />
            </Flex>
            <Box flex="1" minW={0} fontSize="13.5px">
                <Text as="span" fontWeight="600" color={titleColor}>
                    {t('staleBuild.banner.title', {
                        defaultValue: 'A newer version of BarberMate is available.',
                    })}{' '}
                </Text>
                <Text as="span" color={subColor}>
                    {t('staleBuild.banner.detail', {
                        defaultValue: "You're on build {{currentSha}} — refresh to get {{latestSha}}.",
                        currentSha: detail.currentSha,
                        latestSha: detail.latestSha,
                    })}
                </Text>
            </Box>
            <Button
                size="sm"
                variant="ghost"
                fontSize="13px"
                color={subColor}
                onClick={() => {
                    sessionStorage.setItem(SESSION_DISMISS_KEY, detail.latestSha)
                    setBannerVisible(false)
                }}
            >
                {t('staleBuild.banner.later', { defaultValue: 'Later' })}
            </Button>
            <Button
                size="sm"
                colorPalette="orange"
                borderRadius="full"
                onClick={() => {
                    analytics.versionPromptAccepted(
                        detail.currentSha,
                        detail.latestSha,
                        Date.now() - detail.detectedAt,
                        'banner',
                    )
                    window.location.reload()
                }}
            >
                {t('staleBuild.banner.refresh', { defaultValue: 'Refresh now' })}
            </Button>
            <IconButton
                aria-label={t('staleBuild.dismiss', { defaultValue: 'Dismiss' })}
                size="xs"
                variant="ghost"
                color={subColor}
                onClick={() => {
                    sessionStorage.setItem(SESSION_DISMISS_KEY, detail.latestSha)
                    setBannerVisible(false)
                }}
            >
                <LuX size={14} />
            </IconButton>
        </Flex>
    )
}
